import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BarChart3, FileText, FileDown, Calendar, TrendingUp, DollarSign, Users, Ticket, XCircle, Calculator, PieChart, Layers, Package, FileSpreadsheet } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableFooter,
} from "@/components/ui/table";
import { format, subDays, startOfMonth, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { useFiscalEmissionsReport } from "@/hooks/useFiscalEmissionsReport";
import jsPDF from "jspdf";

const reportNavItems = [
  { title: "Vendas", url: "/dashboard/relatorios", icon: BarChart3, description: "Análise de vendas" },
  { title: "DRE", url: "/dashboard/dre", icon: FileSpreadsheet, description: "Demonstrativo de resultado" },
  { title: "Combos", url: "/dashboard/relatorio-combos", icon: Layers, description: "Vendas de combos" },
  { title: "Ticket Médio", url: "/dashboard/ticket-medio", icon: Users, description: "Média por pessoa" },
  { title: "Entradas", url: "/dashboard/relatorio-entradas", icon: Ticket, description: "Couvert e entradas" },
  { title: "Cancelamentos", url: "/dashboard/cancelamentos", icon: XCircle, description: "Itens cancelados" },
  { title: "CMV", url: "/dashboard/cmv", icon: Calculator, description: "Custo de mercadoria" },
  { title: "Margens", url: "/dashboard/margens", icon: PieChart, description: "Lucro por produto" },
  { title: "Por Grupo", url: "/dashboard/relatorio-grupo", icon: Package, description: "Vendas por categoria" },
  { title: "Emissões Fiscais", url: "/dashboard/relatorio-fiscal-emissoes", icon: FileText, description: "NFC-e emitidas" },
];

type DatePreset = "today" | "7days" | "30days" | "thisMonth" | "custom";

export default function RelatorioFiscalEmissoesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [datePreset, setDatePreset] = useState<DatePreset>("30days");
  const [dateFrom, setDateFrom] = useState<Date>(subDays(new Date(), 30));
  const [dateTo, setDateTo] = useState<Date>(new Date());

  const { data, isLoading } = useFiscalEmissionsReport({ dateFrom, dateTo });

  const handlePresetChange = (preset: DatePreset) => {
    setDatePreset(preset);
    const now = new Date();
    switch (preset) {
      case "today": setDateFrom(now); setDateTo(now); break;
      case "7days": setDateFrom(subDays(now, 7)); setDateTo(now); break;
      case "30days": setDateFrom(subDays(now, 30)); setDateTo(now); break;
      case "thisMonth": setDateFrom(startOfMonth(now)); setDateTo(endOfMonth(now)); break;
    }
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);

  const handleExportPDF = () => {
    if (!data?.items.length) return;
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("Relatório de Emissões Fiscais (NFC-e)", 14, 20);
    doc.setFontSize(10);
    doc.text(`Período: ${format(dateFrom, "dd/MM/yyyy")} a ${format(dateTo, "dd/MM/yyyy")}`, 14, 28);

    let y = 40;
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Número", 14, y);
    doc.text("Série", 40, y);
    doc.text("Data", 60, y);
    doc.text("Chave de Acesso", 90, y);
    doc.text("Valor", 180, y, { align: "right" });
    doc.setFont("helvetica", "normal");
    y += 6;

    for (const item of data.items) {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.text(String(item.numero), 14, y);
      doc.text(String(item.serie), 40, y);
      doc.text(format(new Date(item.created_at), "dd/MM/yyyy HH:mm"), 60, y);
      const chave = item.chave_acesso || "—";
      doc.text(chave.length > 30 ? chave.substring(0, 30) + "..." : chave, 90, y);
      doc.text(formatCurrency(item.valor_total), 180, y, { align: "right" });
      y += 5;
    }

    y += 4;
    doc.setFont("helvetica", "bold");
    doc.text(`Total: ${data.totalNotes} notas — ${formatCurrency(data.totalValue)}`, 14, y);
    doc.save(`emissoes-fiscais-${format(dateFrom, "yyyy-MM-dd")}-a-${format(dateTo, "yyyy-MM-dd")}.pdf`);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-warning/10 flex items-center justify-center">
            <FileText className="h-5 w-5 text-warning" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Emissões Fiscais</h1>
            <p className="text-sm text-muted-foreground">NFC-e autorizadas no período</p>
          </div>
        </div>
      </div>

      {/* Report Navigation Hub */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-10 gap-3">
        {reportNavItems.map((item) => {
          const isCurrentPage = location.pathname === item.url;
          return (
            <Card
              key={item.url}
              className={cn(
                "cursor-pointer transition-all hover:shadow-md",
                isCurrentPage
                  ? "border-primary bg-primary/5 ring-1 ring-primary/20"
                  : "hover:border-primary/50"
              )}
              onClick={() => !isCurrentPage && navigate(item.url)}
            >
              <CardContent className="p-3 flex flex-col items-center gap-1 text-center">
                <item.icon className={cn("h-5 w-5", isCurrentPage ? "text-primary" : "text-muted-foreground")} />
                <span className={cn("text-xs font-medium", isCurrentPage && "text-primary")}>{item.title}</span>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Date filters */}
      <div className="flex flex-wrap items-center gap-2">
        {(["today", "7days", "30days", "thisMonth"] as DatePreset[]).map((preset) => (
          <Button
            key={preset}
            variant={datePreset === preset ? "default" : "outline"}
            size="sm"
            onClick={() => handlePresetChange(preset)}
          >
            {preset === "today" && "Hoje"}
            {preset === "7days" && "7 dias"}
            {preset === "30days" && "30 dias"}
            {preset === "thisMonth" && "Este mês"}
          </Button>
        ))}

        <Popover>
          <PopoverTrigger asChild>
            <Button variant={datePreset === "custom" ? "default" : "outline"} size="sm" className="gap-2">
              <Calendar className="h-4 w-4" />
              Personalizado
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <div className="flex">
              <div className="border-r p-3">
                <p className="text-sm font-medium mb-2">De</p>
                <CalendarComponent
                  mode="single"
                  selected={dateFrom}
                  onSelect={(date) => { if (date) { setDateFrom(date); setDatePreset("custom"); } }}
                  locale={ptBR}
                />
              </div>
              <div className="p-3">
                <p className="text-sm font-medium mb-2">Até</p>
                <CalendarComponent
                  mode="single"
                  selected={dateTo}
                  onSelect={(date) => { if (date) { setDateTo(date); setDatePreset("custom"); } }}
                  locale={ptBR}
                />
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={!data?.items.length || isLoading} className="gap-2">
          <FileDown className="h-4 w-4" />
          PDF
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Notas Emitidas</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-20" /> : <p className="text-2xl font-bold">{data?.totalNotes || 0}</p>}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Valor Total Emitido</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {isLoading ? <Skeleton className="h-8 w-32" /> : <p className="text-2xl font-bold">{formatCurrency(data?.totalValue || 0)}</p>}
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle>NFC-e Autorizadas</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : !data?.items.length ? (
            <p className="text-center text-muted-foreground py-8">Nenhuma NFC-e encontrada no período selecionado.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-20">Número</TableHead>
                    <TableHead className="w-16">Série</TableHead>
                    <TableHead className="w-40">Data</TableHead>
                    <TableHead>Chave de Acesso</TableHead>
                    <TableHead className="text-right w-32">Valor</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.numero}</TableCell>
                      <TableCell>{item.serie}</TableCell>
                      <TableCell>{format(new Date(item.created_at), "dd/MM/yyyy HH:mm", { locale: ptBR })}</TableCell>
                      <TableCell className="font-mono text-xs break-all">{item.chave_acesso || "—"}</TableCell>
                      <TableCell className="text-right font-medium">{formatCurrency(item.valor_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={4} className="font-bold">
                      Total ({data.totalNotes} notas)
                    </TableCell>
                    <TableCell className="text-right font-bold">{formatCurrency(data.totalValue)}</TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
